import { useState, useEffect } from 'react';
import { Send, MessageSquare, Phone, Mail, Loader2, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'text-green-600' },
  { value: 'sms', label: 'SMS', icon: Phone, color: 'text-blue-600' },
  { value: 'email', label: 'Email', icon: Mail, color: 'text-purple-600' }
];

export default function SendMessageModal({ 
  open, 
  onClose, 
  recipient,
  recipientType = 'patient' // 'patient' or 'referring_doctor'
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedChannels, setSelectedChannels] = useState(['whatsapp']);
  const [emailSubject, setEmailSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSuccess(false);
      setSelectedTemplate('');
      setEmailSubject('');
    }
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/message-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleChannel = (channel) => {
    setSelectedChannels(prev => {
      if (prev.includes(channel)) {
        if (prev.length === 1) return prev; // Keep at least one channel
        return prev.filter(c => c !== channel);
      }
      return [...prev, channel];
    });
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a message template');
      return;
    }

    if (selectedChannels.includes('email') && !emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    setSending(true);
    try {
      const payload = {
        recipients: [{
          id: recipient.id,
          name: recipient.full_name || recipient.name,
          phone: recipient.phone || recipient.phone_number,
          email: recipient.email,
          recipient_type: recipientType
        }],
        template_id: selectedTemplate,
        channels: selectedChannels,
        subject: emailSubject || null
      };

      const response = await api.post('/messaging/send-bulk', payload);
      
      if (response.data.successful > 0) {
        setSuccess(true);
        toast.success('Message sent successfully!');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectedTemplateContent = templates.find(t => t.id === selectedTemplate)?.content || '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="send-message-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-[#4A238F]" />
            Send Message
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Message Sent!</h3>
            <p className="text-slate-600">
              Your message has been sent to {recipient?.full_name || recipient?.name}
            </p>
          </div>
        ) : (
          <>
            {/* Recipient Info */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-[#4A238F] font-semibold">
                  {(recipient?.full_name || recipient?.name)?.charAt(0) || 'R'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">
                  {recipient?.full_name || recipient?.name}
                </p>
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  {(recipient?.phone || recipient?.phone_number) && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {recipient?.phone || recipient?.phone_number}
                    </span>
                  )}
                  {recipient?.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {recipient?.email}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Channel Selection */}
            <div className="space-y-2">
              <Label>Select Channels</Label>
              <div className="flex gap-2">
                {CHANNELS.map(channel => (
                  <button
                    key={channel.value}
                    type="button"
                    onClick={() => toggleChannel(channel.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      selectedChannels.includes(channel.value)
                        ? 'bg-purple-50 border-[#4A238F] text-[#4A238F]'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <channel.icon className={`h-4 w-4 ${selectedChannels.includes(channel.value) ? channel.color : ''}`} />
                    <span className="text-sm font-medium">{channel.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Message Template *</Label>
              {loading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading templates...
                </div>
              ) : (
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 ? (
                      <SelectItem value="" disabled>No templates available</SelectItem>
                    ) : (
                      templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex flex-col">
                            <span>{template.name}</span>
                            <span className="text-xs text-slate-500">{template.template_type}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
              
              {/* Template Preview */}
              {selectedTemplateContent && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600 max-h-32 overflow-y-auto">
                  <p className="text-xs text-slate-400 mb-1">Preview:</p>
                  {selectedTemplateContent}
                </div>
              )}
            </div>

            {/* Email Subject */}
            {selectedChannels.includes('email') && (
              <div className="space-y-2">
                <Label>Email Subject *</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                  data-testid="email-subject-input"
                />
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button 
                onClick={handleSend} 
                disabled={sending || !selectedTemplate}
                className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                data-testid="send-message-btn"
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Send Message
                  </span>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
